import React, { useMemo } from 'react';
import { OcsMonitorUrl } from '@icons';
import { orderBy } from 'lodash-es';
import { ClinicalGalleryItem, ClinicalNavigation, ClinicalSection } from '@features/components/ClinicalData/components';
import { formatClinicalTime } from '@features/components/ClinicalData/utils';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import { usePreviewFile } from '@features/components/ClinicalData/hooks';
import { formatOCSMonitorData } from '@features/components/ClinicalData/sections/utils';
import { ClinicalData, OrganType } from '@/api/apis/case';
import { getColorsByOrganType } from '@/utils/helpers';
import { useRowScroll } from '@/hooks/useRowScroll';

const label = 'OCS Monitor';

export const OcsMonitorSection: React.FC<{ organType: OrganType; clinicalData: ClinicalData }> = ({
  organType,
  clinicalData,
}) => {
  const { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose } = usePreviewFile();
  const colors = getColorsByOrganType(organType);
  const { listContainerRef, listItemRefs, onNavigationPrev, onNavigationNext, maxLeft, maxRight } = useRowScroll();
  const ocsData = useMemo(() => orderBy(formatOCSMonitorData(clinicalData), 'timestamp'), [clinicalData]);

  return (
    <>
      <ClinicalSection
        color={colors.bg40}
        header={label}
        icon={OcsMonitorUrl}
        endContent={
          ocsData.length > 0 && (
            <ClinicalNavigation
              label={`${ocsData.length} Attachments`}
              prev={onNavigationPrev}
              next={onNavigationNext}
              prevDisabled={maxLeft}
              nextDisabled={maxRight}
            />
          )
        }
      >
        {ocsData.length > 0 && (
          <div
            className="grid grid-flow-col auto-cols-max gap-2 pb-1 relative w-full overflow-auto"
            ref={listContainerRef}
          >
            {ocsData.map((g, i) => (
              <ClinicalGalleryItem
                key={g.file?.fileId}
                ref={(el) => {
                  listItemRefs.current[i] = el;
                }}
                topLabel={g.ocsTime}
                bottomLabel={formatClinicalTime(g.timestamp, g.timeZone)}
                file={g.file}
                onClick={() => handlePreviewOpen(g.file?.fileId)}
              />
            ))}
          </div>
        )}
      </ClinicalSection>
      {isPreviewOpen && (
        <MediaPreview
          heading={label}
          media={ocsData}
          onClose={handlePreviewClose}
          open={isPreviewOpen}
          selectedMediaId={previewFileId}
        />
      )}
    </>
  );
};
